/**
 * The publisher's own step numbers, left on the front of scraped step text (#794).
 *
 * The app numbers steps itself, so a number the page printed reads twice:
 * « 01  1Dans un bol… ». Pages print it three ways, and the scrapers keep all
 * three:
 *
 *  - punctuated: "1. Préchauffer", "2) Mélanger" — always a step number;
 *  - spaced:     "2 Déposer les cubes", "1\n    Dans une sauteuse";
 *  - glued:      "1Dans un bol", "01Dans un bol" — the page's `<span>01</span>`
 *                flattened into the text by `textContent`.
 *
 * A bare number is not always a step number: "2 tasses de farine" can open a
 * step. So spaced and glued numbers are only removed when they number the LIST
 * — at least two of them, counting up by one from 1 (a restart at 1 is a new
 * section) — and only in front of a capital, which is how a step opens and how
 * a quantity ("2 tasses") does not.
 *
 * Every screen that shows step text reads it through here, so the detail
 * screen, cooking mode and the planning timeline agree on what a step says.
 */

const PUNCTUATED_RE = /^\s*\d{1,3}[.)](?!\d)\s*/;
const BARE_RE = /^\s*(\d{1,2})\s*(?=\p{Lu})/u;

export interface StepNumbering {
  /** Each text with the publisher's number removed (otherwise untouched). */
  texts: string[];
  /**
   * Entries whose spaced or glued number counted the list. The publisher made
   * each of those a step, so a short one is still a step and not a section
   * label (« 11 Préchauffer le four à 400˚F » is not a heading). A punctuated
   * "1." is not reported: it has always been stripped without that meaning,
   * and recipes rendering correctly today must not change.
   */
  numbered: boolean[];
}

/** Whether the bare numbers found, in order, count up by one from 1. */
function countsUp(numbers: number[]): boolean {
  if (numbers.length < 2 || numbers[0] !== 1) return false;
  return numbers.every((n, i) => i === 0 || n === numbers[i - 1] + 1 || n === 1);
}

/** True when the list carries spaced or glued step numbers that would be removed. */
export function hasBareStepNumbers(texts: string[]): boolean {
  const numbers = texts
    .map(text => (text || '').match(BARE_RE))
    .filter((m): m is RegExpMatchArray => m !== null)
    .map(m => Number(m[1]));
  return countsUp(numbers);
}

export function stripStepNumbers(texts: string[]): StepNumbering {
  const bare = hasBareStepNumbers(texts);
  const out: string[] = [];
  const numbered: boolean[] = [];
  for (const raw of texts) {
    const text = raw || '';
    const punctuated = text.match(PUNCTUATED_RE);
    if (punctuated) {
      out.push(text.slice(punctuated[0].length));
      numbered.push(false);
      continue;
    }
    const spaced = bare ? text.match(BARE_RE) : null;
    if (spaced) {
      out.push(text.slice(spaced[0].length));
      numbered.push(true);
      continue;
    }
    out.push(text);
    numbered.push(false);
  }
  return { texts: out, numbered };
}

/**
 * A heading that only says "here come the steps" — « Étapes à suivre »,
 * "Steps to follow". Scraped as the first entry of the list, it is not a section
 * of the recipe, just the page's title for the list the app already titles.
 */
const GENERIC_STEPS_LABEL_RE =
  /^(?:[ée]tapes?(?: à suivre)?|marche à suivre|mode de pr[ée]paration|pr[ée]paration|instructions?|directions?|method|steps?(?: to follow)?)\s*:?$/i;

export function isGenericStepsLabel(text: string): boolean {
  return GENERIC_STEPS_LABEL_RE.test((text || '').trim());
}
